import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../auth';
import { UserRole } from '../../types';
import { ChangePasswordModal } from '../../components/ChangePasswordModal';
import { fetchAPI } from '../../services/apiClient';
import { obtenerPendientesGanador } from '../../services/pendientesGanador';
import { getSaldoTone, getSaldoTextColor, getSaldoBgColor } from '../../utils/saldoColor';
import {
  Menu,
  X,
  Home,
  Users,
  Store,
  CreditCard,
  FileText,
  Settings,
  LogOut,
  ChevronDown,
  DollarSign,
  ShoppingCart,
  BarChart3,
  List,
  Award,
  Ban,
  Trophy,
  Wallet,
  UserCircle,
  Building2,
  ClipboardList,
  Calculator,
  Ticket,
  Printer,
  Smartphone,
  Lock,
  KeyRound,
  ClipboardCheck,
  Package,
} from 'lucide-react';
import { Activity } from 'lucide-react';
import { isNativePlatform } from '../../utils/platform';

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  children?: { label: string; path: string }[];
}

// Navegación por rol
const navigationByRole: Record<UserRole, NavItem[]> = {
  ADMIN: [
    { label: 'Dashboard', path: '/admin', icon: <Home size={20} /> },
    { label: 'Bancas', path: '/admin/bancas', icon: <Building2 size={20} /> },
    { label: 'Usuarios', path: '/admin/usuarios', icon: <Users size={20} /> },
    { label: 'Suscripciones', path: '/admin/suscripciones', icon: <CreditCard size={20} /> },
    { label: 'Pagos', path: '/admin/pagos', icon: <DollarSign size={20} /> },
    { label: 'Seguridad', path: '/admin/seguridad', icon: <Lock size={20} /> },
  ],
  BANCA: [
    { label: 'Dashboard', path: '/banca/dashboard', icon: <Home size={20} /> },
    { label: 'Sorteos', path: '/banca/sorteos', icon: <Trophy size={20} /> },
    { label: 'Listas', path: '/banca/listas', icon: <ClipboardList size={20} /> },
    { label: 'Listas Manuales', path: '/banca/listas-manuales', icon: <ClipboardList size={20} /> },
    { label: 'Saldos', path: '/banca/saldos', icon: <Wallet size={20} /> },
    { label: 'Vendedores', path: '/banca/vendedores', icon: <Users size={20} /> },
    { label: 'Números Premiados', path: '/banca/premios', icon: <Award size={20} /> },
    { label: 'Paquetes de Reglas', path: '/banca/paquetes-reglas', icon: <Package size={20} /> },
    { label: 'Ganadores', path: '/banca/ganadores', icon: <Trophy size={20} /> },
    { label: 'Reportes', path: '/banca/reportes', icon: <BarChart3 size={20} /> },
    { label: 'Sorteos por Local', path: '/banca/locales-sorteos', icon: <Store size={20} /> },
    { label: 'Auditoría', path: '/banca/auditoria', icon: <ClipboardCheck size={20} /> },
    { label: 'Analítica', path: '/banca/analitica', icon: <Activity size={20} /> },
  ],
  LOCAL: [
    { label: 'Panel', path: '/local/dashboard', icon: <Home size={20} /> },
    { label: 'POS', path: '/local/pos', icon: <ShoppingCart size={20} /> },
    { label: 'Reportes', path: '/local/reportes', icon: <BarChart3 size={20} /> },
    { label: 'Historial', path: '/local/historico', icon: <FileText size={20} /> },
    { label: 'Saldo', path: '/local/saldos', icon: <Wallet size={20} /> },
    { label: 'Listas', path: '/local/listas', icon: <ClipboardList size={20} /> },
    { label: 'Ganadores', path: '/local/ganadores', icon: <Trophy size={20} /> },
    { label: 'Ticket', path: '/local/ticket-config', icon: <Printer size={20} /> },
    ...(isNativePlatform() ? [{ label: 'Impresora', path: '/local/printer-config', icon: <Smartphone size={20} /> }] : []),
  ],
  VENDEDOR_CON_CAJA: [
    { label: 'POS', path: '/local/pos', icon: <ShoppingCart size={20} /> },
    { label: 'Reportes', path: '/local/reportes', icon: <BarChart3 size={20} /> },
    { label: 'Saldo', path: '/local/saldos', icon: <Wallet size={20} /> },
    { label: 'Listas', path: '/local/listas', icon: <ClipboardList size={20} /> },
    { label: 'Ganadores', path: '/local/ganadores', icon: <Trophy size={20} /> },
    { label: 'Ticket', path: '/local/ticket-config', icon: <Printer size={20} /> },
    ...(isNativePlatform() ? [{ label: 'Impresora', path: '/local/printer-config', icon: <Smartphone size={20} /> }] : []),
  ],
  VENDEDOR_SIN_CAJA: [
    { label: 'POS', path: '/local/pos', icon: <ShoppingCart size={20} /> },
    { label: 'Reportes', path: '/local/reportes', icon: <BarChart3 size={20} /> },
    { label: 'Saldo', path: '/local/saldos', icon: <Wallet size={20} /> },
    { label: 'Listas', path: '/local/listas', icon: <ClipboardList size={20} /> },
    { label: 'Ganadores', path: '/local/ganadores', icon: <Trophy size={20} /> },
    { label: 'Ticket', path: '/local/ticket-config', icon: <Printer size={20} /> },
    ...(isNativePlatform() ? [{ label: 'Impresora', path: '/local/printer-config', icon: <Smartphone size={20} /> }] : []),
  ],
  // MINI_BANCA: módulo propio. Vende como local (POS/Ticket) y administra su
  // ecosistema interno con pantallas propias. NO ve pantallas de banca
  // (sorteos, ganadores, configuración, locales, reportes globales).
  MINI_BANCA: [
    { label: 'Panel', path: '/minibanca', icon: <Home size={20} /> },
    { label: 'POS', path: '/local/pos', icon: <ShoppingCart size={20} /> },
    { label: 'Mis vendedores', path: '/minibanca/vendedores', icon: <Users size={20} /> },
    { label: 'Paquetes internos', path: '/minibanca/paquetes', icon: <Package size={20} /> },
    { label: 'Listas de vendedores', path: '/minibanca/listas', icon: <ClipboardList size={20} /> },
    { label: 'Listas Manuales', path: '/minibanca/listas-manuales', icon: <ClipboardList size={20} /> },
    { label: 'Reportes internos', path: '/minibanca/reportes', icon: <BarChart3 size={20} /> },
    { label: 'Saldos de vendedores', path: '/minibanca/saldos', icon: <Wallet size={20} /> },
    { label: 'Historial', path: '/local/historico', icon: <FileText size={20} /> },
    { label: 'Ticket', path: '/local/ticket-config', icon: <Printer size={20} /> },
    ...(isNativePlatform() ? [{ label: 'Impresora', path: '/local/printer-config', icon: <Smartphone size={20} /> }] : []),
  ],
};

interface SaldoVisible {
  local_nombre?: string;
  saldo_actual: string | null;
  es_propio?: boolean;
  credito?: {
    saldo_post_sorteo?: string;
  } | null;
}

export const AppLayout: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [saldoVisible, setSaldoVisible] = useState<SaldoVisible | null>(null);
  const [pendientesCount, setPendientesCount] = useState(0);

  if (!user) return null;

  const navItems = navigationByRole[user.role] || [];
  const canChangePassword = ['ADMIN', 'BANCA', 'LOCAL', 'MINI_BANCA'].includes(user.role);
  const encabezadoSidebar = user.role === 'BANCA'
    ? {
        titulo: 'Tiempos Barahona',
        subtitulo: 'Panel de banca',
        logo: (
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-50 to-yellow-100 border border-amber-200 shadow-sm flex items-center justify-center overflow-hidden">
            <img
              src="/logo-barahona-b.svg"
              alt="Tiempos Barahona"
              className="w-10 h-10 object-contain"
            />
          </div>
        ),
      }
    : {
        titulo: 'TiemPos',
        subtitulo: 'v2.0',
        logo: (
          <div className="w-10 h-10 bg-primary-600 rounded-xl flex items-center justify-center">
            <span className="text-white font-bold text-lg">T</span>
          </div>
        ),
      };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const toggleExpanded = (label: string) => {
    setExpandedItems(prev => 
      prev.includes(label) 
        ? prev.filter(i => i !== label)
        : [...prev, label]
    );
  };

  const isActive = (path: string) => location.pathname === path;
  const isActiveParent = (item: NavItem) => {
    if (item.children) {
      return item.children.some(c => location.pathname.startsWith(c.path));
    }
    return location.pathname.startsWith(item.path);
  };

  useEffect(() => {
    let cancelled = false;
    const rolesConSaldo = ['LOCAL', 'MINI_BANCA'];
    if (!rolesConSaldo.includes(user.role)) {
      setSaldoVisible(null);
      return;
    }

    const cargarSaldo = async () => {
      try {
        const data = await fetchAPI<SaldoVisible>('/saldos/mi-saldo');
        if (!cancelled) setSaldoVisible(data);
      } catch {
        if (!cancelled) setSaldoVisible(null);
      }
    };

    cargarSaldo();
    const onRefresh = () => void cargarSaldo();
    window.addEventListener('tiemposb:saldos-actualizados', onRefresh);
    return () => {
      cancelled = true;
      window.removeEventListener('tiemposb:saldos-actualizados', onRefresh);
    };
  }, [location.pathname, user.role]);

  // Pendientes ganador (solo BANCA)
  // Se pide a través de `obtenerPendientesGanador`, que coalesce esta llamada
  // con la que hace SaldosPage al entrar a /banca/saldos: una sola solicitud
  // HTTP en lugar de dos idénticas en paralelo.
  useEffect(() => {
    if (user.role !== 'BANCA') return;
    let cancelled = false;
    const cargar = async () => {
      try {
        const data = await obtenerPendientesGanador();
        if (!cancelled) setPendientesCount(data.length);
      } catch {
        if (!cancelled) setPendientesCount(0);
      }
    };
    cargar();
    return () => { cancelled = true; };
  }, [location.pathname, user.role]);

  // Sidebar content
  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="flex items-center gap-3 px-6 py-5 border-b border-gray-100">
        {encabezadoSidebar.logo}
        <div className="flex-1">
          <h1 className="font-bold text-gray-900 leading-tight">{encabezadoSidebar.titulo}</h1>
          <p className="text-xs text-gray-500">{encabezadoSidebar.subtitulo}</p>
        </div>
        <button
          onClick={() => setSidebarOpen(false)}
          className="lg:hidden p-2.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center"
          aria-label="Cerrar menú"
        >
          <X size={20} />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-3">
        <ul className="space-y-1">
          {navItems.map(item => (
            <li key={item.path}>
              {item.children ? (
                <div>
                  <button
                    onClick={() => toggleExpanded(item.label)}
                    className={`
                      w-full flex items-center justify-between px-3 py-2.5 rounded-lg
                      transition-colors text-sm font-medium
                      ${isActiveParent(item) 
                        ? 'bg-primary-50 text-primary-700' 
                        : 'text-gray-600 hover:bg-gray-100'
                      }
                    `}
                  >
                    <span className="flex items-center gap-3">
                      {item.icon}
                      {item.label}
                    </span>
                    <ChevronDown 
                      size={16} 
                      className={`transition-transform ${expandedItems.includes(item.label) ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {expandedItems.includes(item.label) && (
                    <ul className="mt-1 ml-9 space-y-1">
                      {item.children.map(child => (
                        <li key={child.path}>
                          <Link
                            to={child.path}
                            onClick={() => setSidebarOpen(false)}
                            className={`
                              block px-3 py-2 rounded-lg text-sm
                              ${isActive(child.path)
                                ? 'bg-primary-50 text-primary-700 font-medium'
                                : 'text-gray-600 hover:bg-gray-100'
                              }
                            `}
                          >
                            {child.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : (
                <Link
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center gap-3 px-3 py-2.5 rounded-lg
                    transition-colors text-sm font-medium
                    ${isActive(item.path) 
                      ? 'bg-primary-50 text-primary-700' 
                      : 'text-gray-600 hover:bg-gray-100'
                    }
                  `}
                >
                  {item.icon}
                  {item.label}
                  {item.path === '/banca/premios' && pendientesCount > 0 && (
                    <span className="ml-auto bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                      {pendientesCount}
                    </span>
                  )}
                </Link>
              )}
            </li>
          ))}
        </ul>
      </nav>

      {/* User section */}
      <div className="border-t border-gray-100 p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
            <UserCircle className="w-6 h-6 text-gray-500" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{user.nombre}</p>
            <p className="text-xs text-gray-500 truncate">{user.role.replace('_', ' ')}</p>
          </div>
        </div>
        {canChangePassword && (
          <button
            onClick={() => setShowChangePassword(true)}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 mb-2 text-sm text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
          >
            <KeyRound size={18} />
            Cambiar contraseña
          </button>
        )}
        {isNativePlatform() && (
          <button
            onClick={() => navigate('/servidor?from=app')}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 mb-2 text-sm text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
          >
            <Settings size={18} />
            Servidor
          </button>
        )}
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm text-gray-600 hover:text-danger-600 hover:bg-danger-50 rounded-lg transition-colors"
        >
          <LogOut size={18} />
          Cerrar sesión
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Mobile */}
      <aside 
        className={`
          fixed left-0 top-0 h-full w-72 max-w-[calc(100vw-2rem)] bg-white z-50 shadow-xl
          transform transition-transform duration-300 ease-out lg:hidden
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <SidebarContent />
      </aside>

      {/* Sidebar - Desktop */}
      <aside className="hidden lg:fixed lg:left-0 lg:top-0 lg:h-full lg:w-64 lg:bg-white lg:border-r lg:border-gray-200 lg:flex lg:flex-col">
        <SidebarContent />
      </aside>

      {/* Main content */}
      <div className="min-w-0 lg:ml-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-white border-b border-gray-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2.5 text-gray-600 hover:bg-gray-100 rounded-lg lg:hidden min-h-[44px] min-w-[44px] flex items-center justify-center"
            >
              <Menu size={24} />
            </button>

            <div className="flex-1 lg:flex-none" />

            <div className="flex items-center gap-3">
              {saldoVisible && (() => {
                // Propio: saldo_real (saldo_actual), No propio: saldo_post_sorteo
                const saldoDisplay = saldoVisible.es_propio
                  ? Number(saldoVisible.saldo_actual ?? 0)
                  : Number(saldoVisible.credito?.saldo_post_sorteo ?? saldoVisible.saldo_actual ?? 0);
                const _tone = getSaldoTone(saldoDisplay);
                const _bgBorder = getSaldoBgColor(_tone);
                const _text = getSaldoTextColor(_tone);
                return (
                <div className={`hidden rounded-full px-4 py-2 text-right sm:block border ${_bgBorder}`}>
                  <p className={`text-[11px] uppercase tracking-wide ${_text}`}>Mi saldo</p>
                  <p className={`text-sm font-semibold ${_text}`}>₡{saldoDisplay.toLocaleString('es-CR')}</p>
                </div>
                );
              })()}
              <span className="text-sm text-gray-500 hidden sm:block">
                {new Date().toLocaleDateString('es-CR', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </span>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="min-w-0 p-4 lg:p-6">
          <Outlet />
        </main>
      </div>

      {/* Modal de cambiar contraseña */}
      {canChangePassword && (
        <ChangePasswordModal
          isOpen={showChangePassword}
          onClose={() => setShowChangePassword(false)}
        />
      )}
    </div>
  );
};

export default AppLayout;
